import React, { useState, useEffect } from "react";
import {
  FaShoppingCart,
  FaCalendarAlt,
  FaWallet,
  FaHandHoldingUsd,
  FaBoxes,
  FaFileInvoiceDollar,
  FaExclamationTriangle,
  FaArrowUp,
  FaArrowDown,
  FaReceipt,
} from "react-icons/fa";
import apiRequest from "../services/api";

function formatCurrency(value) {
  const num = Number(value || 0);
  if (Math.abs(num) >= 1_000_000) return `ETB ${(num / 1_000_000).toFixed(2)}M`;
  if (Math.abs(num) >= 1_000) return `ETB ${(num / 1_000).toFixed(0)}K`;
  return `ETB ${num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

function formatFullCurrency(value) {
  return `ETB ${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function formatToday() {
  return new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function LineChart({ data, height = 120 }) {
  const max = Math.max(...data, 1);
  const w = 320;
  const h = height;
  const stepX = w / (data.length - 1 || 1);
  const points = data
    .map((v, i) => `${i * stepX},${h - (v / max) * (h - 16) - 8}`)
    .join(" ");

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className="w-full h-28"
      preserveAspectRatio="none"
    >
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinejoin="round"
        strokeLinecap="round"
        points={points}
        className="text-emerald-500"
      />
      {data.map((v, i) => (
        <circle
          key={i}
          cx={i * stepX}
          cy={h - (v / max) * (h - 16) - 8}
          r="3"
          className="fill-emerald-500"
        />
      ))}
    </svg>
  );
}

function BarCompare({ revenue, expense, labels }) {
  const max = Math.max(...revenue, ...expense, 1);
  const barW = 18;
  const gap = 28;
  const h = 110;

  return (
    <svg
      viewBox={`0 0 ${labels.length * gap + 20} ${h + 24}`}
      className="w-full h-32"
    >
      {labels.map((label, i) => {
        const x = 12 + i * gap;
        const rH = (revenue[i] / max) * h;
        const eH = (expense[i] / max) * h;
        return (
          <g key={label}>
            <rect
              x={x}
              y={h - rH}
              width={barW / 2 - 1}
              height={rH}
              rx="2"
              className="fill-emerald-500"
            />
            <rect
              x={x + barW / 2 + 1}
              y={h - eH}
              width={barW / 2 - 1}
              height={eH}
              rx="2"
              className="fill-rose-400"
            />
            <text
              x={x + barW / 2}
              y={h + 14}
              textAnchor="middle"
              className="fill-slate-500 dark:fill-slate-400 text-[8px]"
            >
              {label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/**
 * HomeDashboard
 * Priority: dataProp → API data
 */
export default function HomeDashboard({ data: dataProp, className = "" }) {
  const [trendRange, setTrendRange] = useState("30D");
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const rangeBtns = [
    { value: "7D", label: "7 Days" },
    { value: "30D", label: "30 Days" },
    { value: "3M", label: "3 Months" },
    { value: "12M", label: "12 Months" },
  ];

  // Load from API
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiRequest(`reports/dashboard?range=${trendRange}`);
        if (!cancelled) {
          if (res?.data) {
            setApiData(res.data);
          } else {
            setApiData(null);
            setError("No data received from server");
          }
        }
      } catch (err) {
        if (!cancelled) {
          setApiData(null);
          setError(err?.message || "Failed to load dashboard data");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [trendRange]);

  // Priority: prop → API
  const data = dataProp || apiData;

  const kpiCards = data
    ? [
        {
          title: "Today's Sales",
          value: formatCurrency(data.todaySales),
          change: data.todaySalesChange,
          icon: <FaShoppingCart />,
          color: "text-emerald-600 dark:text-emerald-400",
          bg: "bg-emerald-50 dark:bg-emerald-500/10",
        },
        {
          title: "Monthly Sales",
          value: formatCurrency(data.monthlySales),
          change: data.monthlySalesChange,
          icon: <FaCalendarAlt />,
          color: "text-blue-600 dark:text-blue-400",
          bg: "bg-blue-50 dark:bg-blue-500/10",
        },
        {
          title: "Cash & Bank",
          value: formatCurrency(data.cashAndBank),
          icon: <FaWallet />,
          color: "text-purple-600 dark:text-purple-400",
          bg: "bg-purple-50 dark:bg-purple-500/10",
        },
        {
          title: "Receivables",
          value: formatCurrency(data.receivables),
          icon: <FaHandHoldingUsd />,
          color: "text-amber-600 dark:text-amber-400",
          bg: "bg-amber-50 dark:bg-amber-500/10",
        },

        {
          title: "Payables",
          value: formatCurrency(data.payables),
          icon: <FaFileInvoiceDollar />,
          color: "text-rose-600 dark:text-rose-400",
          bg: "bg-rose-50 dark:bg-rose-500/10",
        },
        {
          title: "Inventory",
          value: formatCurrency(data.inventoryValue),
          icon: <FaBoxes />,
          color: "text-cyan-600 dark:text-cyan-400",
          bg: "bg-cyan-50 dark:bg-cyan-500/10",
        },
      ]
    : [];

  return (
    <div
      className={`min-h-full relative overflow-hidden font-sans p-3 md:p-6 bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-100 ${className}`}
    >
      <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full pointer-events-none bg-blue-500/5 blur-[120px] dark:bg-blue-500/10" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] rounded-full pointer-events-none bg-emerald-500/5 blur-[150px] dark:bg-emerald-500/10" />

      <div className="max-w-7xl mx-auto relative z-10 space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-5 rounded-2xl border shadow-sm backdrop-blur-xl bg-white border-slate-200 dark:bg-white/[0.03] dark:border-white/[0.08]">
          <div>
            <h1 className="text-xl md:text-2xl font-extrabold text-slate-900 dark:text-white">
              {getGreeting()}
              {data?.businessName ? `, ${data.businessName}` : ""}
            </h1>
          </div>

          {/* Right side – Sales Trend range */}
          <div className="flex items-center gap-3">
            <div className="flex gap-1 p-1 rounded-xl bg-slate-100 dark:bg-slate-900/60 border border-slate-200 dark:border-white/[0.06]">
              {rangeBtns.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setTrendRange(r.value)}
                  className={`px-3 py-1 rounded-lg text-xs font-bold transition ${
                    trendRange === r.value
                      ? "bg-emerald-600 text-white shadow"
                      : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Loading state */}
        {loading && !data && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="inline-block w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mb-3" />
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Loading dashboard...
              </p>
            </div>
          </div>
        )}

        {/* Error state (when no data) */}
        {!loading && !data && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center p-6 rounded-2xl border bg-white border-slate-200 dark:bg-white/[0.02] dark:border-white/[0.08]">
              <FaExclamationTriangle className="mx-auto text-2xl text-rose-500 mb-3" />
              <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                {error || "Unable to load dashboard data"}
              </p>
              <button
                type="button"
                onClick={() => setTrendRange((r) => r)} // triggers re-fetch
                className="mt-4 px-4 py-2 text-xs font-bold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Main content – only when we have data */}
        {data && (
          <>
            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {kpiCards.map((k) => (
                <div
                  key={k.title}
                  className="p-4 rounded-2xl border backdrop-blur-xl bg-white border-slate-200 shadow-sm dark:bg-white/[0.02] dark:border-white/[0.08]"
                >
                  <div
                    className={`inline-flex p-2 rounded-lg mb-2 ${k.bg} ${k.color}`}
                  >
                    {k.icon}
                  </div>
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">
                    {k.title}
                  </p>
                  <p className={`text-lg font-bold mt-0.5 ${k.color}`}>
                    {k.value}
                  </p>
                  {k.change != null && (
                    <p
                      className={`text-[11px] mt-1 flex items-center gap-1 font-medium ${
                        k.change >= 0
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-rose-600 dark:text-rose-400"
                      }`}
                    >
                      {k.change >= 0 ? <FaArrowUp /> : <FaArrowDown />}
                      {Math.abs(k.change)}%
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* Sales trend */}
            <div className="p-5 rounded-2xl border shadow-sm backdrop-blur-xl bg-white border-slate-200 dark:bg-white/[0.02] dark:border-white/[0.08]">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200">
                  Sales Trend
                </h2>
              </div>
              <LineChart data={data.salesTrend || []} />
            </div>

            {/* Revenue vs Expenses + P&L */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="p-5 rounded-2xl border shadow-sm bg-white border-slate-200 dark:bg-white/[0.02] dark:border-white/[0.08]">
                <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200 mb-3">
                  Revenue vs Expenses
                </h2>
                <div className="flex items-center gap-4 mb-2 text-[10px]">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />{" "}
                    Revenue
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm bg-rose-400" />{" "}
                    Expenses
                  </span>
                </div>
                <BarCompare
                  revenue={data.revenueVsExpense?.revenue || []}
                  expense={data.revenueVsExpense?.expense || []}
                  labels={data.revenueVsExpense?.labels || []}
                />
              </div>

              <div className="p-5 rounded-2xl border shadow-sm bg-white border-slate-200 dark:bg-white/[0.02] dark:border-white/[0.08]">
                <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200 mb-4">
                  Profit & Loss
                </h2>
                <div className="space-y-3">
                  {[
                    {
                      label: "Revenue",
                      value: data.pnl?.revenue,
                      color: "text-emerald-600 dark:text-emerald-400",
                    },
                    {
                      label: "COGS",
                      value: data.pnl?.cogs,
                      color: "text-rose-600 dark:text-rose-400",
                    },

                    {
                      label: "Gross Profit",
                      value: data.pnl?.grossProfit,
                      color: "text-blue-600 dark:text-blue-400",
                    },
                    {
                      label: "Expense",
                      value: data.pnl?.expenses,
                      color: "text-rose-600 dark:text-rose-400",
                    },
                    {
                      label: "Net Profit",
                      value: data.pnl?.netProfit,
                      color:
                        "text-emerald-700 dark:text-emerald-300 font-extrabold",
                    },
                  ].map((row) => (
                    <div
                      key={row.label}
                      className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-white/[0.05] last:border-0"
                    >
                      <span className="text-sm text-slate-600 dark:text-slate-300">
                        {row.label}
                      </span>
                      <span
                        className={`font-mono font-bold text-sm ${row.color}`}
                      >
                        {formatCurrency(row.value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Aging + Inventory alerts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="p-5 rounded-2xl border shadow-sm bg-white border-slate-200 dark:bg-white/[0.02] dark:border-white/[0.08]">
                <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200 mb-4">
                  Receivables Aging
                </h2>
                <div className="space-y-2.5">
                  {(data.aging || []).map((a) => (
                    <div
                      key={a.label}
                      className="flex justify-between items-center"
                    >
                      <span className="text-sm text-slate-600 dark:text-slate-300">
                        {a.label}
                      </span>
                      <span className="font-mono font-semibold text-sm text-slate-800 dark:text-slate-100">
                        {formatCurrency(a.amount)}
                      </span>
                    </div>
                  ))}
                  <div className="pt-2 mt-2 border-t border-slate-100 dark:border-white/[0.06] flex justify-between font-bold">
                    <span className="text-xs uppercase text-slate-500">
                      Total AR
                    </span>
                    <span className="font-mono text-amber-600 dark:text-amber-400">
                      {formatCurrency(
                        (data.aging || []).reduce(
                          (s, a) => s + Number(a.amount || 0),
                          0
                        )
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-5 rounded-2xl border shadow-sm bg-white border-slate-200 dark:bg-white/[0.02] dark:border-white/[0.08]">
                <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200 mb-4">
                  Inventory Alerts
                </h2>
                <div className="space-y-3">
                  {(data.inventoryAlerts || []).map((a) => (
                    <div
                      key={a.type}
                      className="flex items-center justify-between p-3 rounded-xl border bg-slate-50 border-slate-200 dark:bg-white/[0.03] dark:border-white/[0.06]"
                    >
                      <div className="flex items-center gap-2">
                        <FaExclamationTriangle className={a.color} />
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                          {a.label}
                        </span>
                      </div>
                      <span
                        className={`text-lg font-bold font-mono ${a.color}`}
                      >
                        {a.count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Top products + Tax */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="p-5 rounded-2xl border shadow-sm bg-white border-slate-200 dark:bg-white/[0.02] dark:border-white/[0.08]">
                <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200 mb-4">
                  Top Products
                </h2>
                <div className="space-y-2.5">
                  {(data.topProducts || []).map((p, i) => (
                    <div
                      key={p.name}
                      className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-white/[0.05] last:border-0"
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-slate-100 dark:bg-white/10 flex items-center justify-center text-[10px] font-bold text-slate-500">
                          {i + 1}
                        </span>
                        <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
                          {p.name}
                        </span>
                      </div>
                      <span className="font-mono text-sm text-slate-600 dark:text-slate-300">
                        {Number(p.qty || 0).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-5 rounded-2xl border shadow-sm bg-white border-slate-200 dark:bg-white/[0.02] dark:border-white/[0.08]">
                <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200 mb-4">
                  Tax Summary
                </h2>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600 dark:text-slate-300">
                      Output VAT
                    </span>
                    <span className="font-mono font-semibold text-sm text-slate-800 dark:text-slate-100">
                      {formatCurrency(data.tax?.outputVat)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600 dark:text-slate-300">
                      Input VAT
                    </span>
                    <span className="font-mono font-semibold text-sm text-emerald-600 dark:text-emerald-400">
                      {formatCurrency(data.tax?.inputVat)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600 dark:text-slate-300">
                      VAT Withholding
                    </span>
                    <span className="font-mono font-semibold text-sm text-emerald-600 dark:text-emerald-400">
                      {formatCurrency(data.tax?.vatWithhold)}
                    </span>
                  </div>
                  <div className="pt-2 border-t border-slate-100 dark:border-white/[0.06] flex justify-between font-bold">
                    <span className="text-sm text-slate-700 dark:text-slate-200">
                      VAT Payable
                    </span>
                    <span className="font-mono text-rose-600 dark:text-rose-400">
                      {formatCurrency(data.tax?.vatPayable)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent transactions */}
            <div className="p-5 rounded-2xl border shadow-sm bg-white border-slate-200 dark:bg-white/[0.02] dark:border-white/[0.08]">
              <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200 mb-4">
                Recent Transactions
              </h2>
              <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-white/[0.06]">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider bg-slate-50 text-slate-500 border-b border-slate-200 dark:bg-white/[0.02] dark:text-slate-400 dark:border-white/[0.06]">
                      <th className="p-3">Date</th>
                      <th className="p-3">Reference</th>
                      <th className="p-3">Party</th>
                      <th className="p-3">Type</th>
                      <th className="p-3 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-white/[0.04]">
                    {(data.recentTx || []).map((tx) => (
                      <tr
                        key={tx.id}
                        className="hover:bg-slate-50 dark:hover:bg-white/[0.02] transition"
                      >
                        <td className="p-3 font-mono text-xs text-slate-500 whitespace-nowrap">
                          {new Date(tx.date).toLocaleString()}
                        </td>
                        <td className="p-3">
                          <span className="inline-flex items-center gap-1.5 font-semibold text-slate-700 dark:text-slate-200">
                            <FaReceipt className="text-slate-400 text-xs" />
                            {tx.ref}
                          </span>
                        </td>
                        <td className="p-3 text-slate-600 dark:text-slate-300">
                          {tx.party}
                        </td>
                        <td className="p-3">
                          <span
                            className={`text-[10px] font-bold px-2 py-0.5 rounded border ${
                              tx.type === "Sale"
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20"
                                : "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/20"
                            }`}
                          >
                            {tx.type}
                          </span>
                        </td>
                        <td
                          className={`p-3 text-right font-mono font-bold ${
                            tx.amount >= 0
                              ? "text-emerald-600 dark:text-emerald-400"
                              : "text-rose-600 dark:text-rose-400"
                          }`}
                        >
                          {formatFullCurrency(tx.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
